import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { fxShockRing } from '../render/weapon-fx';
import { disposeGadget, makeOrbitBlades, setBladeRadius } from '../render/gadgets';
import { areaDamage, damageEnemy, nearestEnemy, shake, spawnRing } from '../sim/fx';
import { lockedTarget } from '../sim/lockon';
import { spawnBullet } from '../sim/weapons';
import { safeSpot } from '../world/arena';
import { P } from '../world/player';
import { mines, strikes } from '../world/pools';
import type { Evolution } from './evolutions';
/* ============================================================
   進化 · 第一批（2026-08-02）
   ============================================================

   設計原則（進化 = 解除限制）與匯總在 `data/evolutions.ts`，這裡只有定義。
   第二批在 `data/evolutions-b.ts` —— 拆檔的理由是單檔 300 行上限。

   這四條的鑰匙分別是傷害／冷卻／衝擊／衝擊軸的被動。後兩條（穿甲協定、
   軌道支配）都掛在**衝擊軸**上，那條軸線因此有自己的頂點，
   而不是只有一堆百分比。 */

export var EVOLUTIONS_A: Evolution[] = [
  {
    /* 磁暴地雷 + 核心超載 → 磁暴雷場。
       **拿掉的限制：「一次佈設 N 顆然後等冷卻」。**
       原本地雷最煩的地方是節奏 —— 放一圈、等 5 秒、再放一圈，
       中間那段空窗玩家什麼都做不了。進化之後冷卻縮到 1.4 秒且不看數量，
       結果是**腳下永遠有一層雷毯**，「佈場」從一個動作變成一個狀態。 */
    id: 'e-minefield', n: '磁暴雷場', kind: '進化', axes: ['範圍', '持續'],
    evo: { from: 'w-mine', needs: 'p-power' },
    turn: 3, turnD: '腳下的雷毯 → 遠端投放（一半的雷直接佈在敵群腳下）',
    d: function (lv) {
      return '**不再有佈設冷卻**：每次補 ' + (2 + lv) + ' 顆（單顆 ' + (118 + lv * 14) + '），腳下永遠是一片雷區'
        + (lv < 3 ? '' : '；**一半直接投到敵群腳下**');
    },
    cd: function () { return 0.85; },
    fire: function (lv) {
      /* 冷卻短、每次補三顆 —— 結果是玩家周圍永遠有一層雷毯，
         而不是「一次放一圈然後等 5 秒」。

         ⚠️ 2026-08-05 配平（`pnpm crafts` 量到 ×0.32，也就是**進化之後變弱**）。
         原因是只看了「冷卻變短」沒看總量：舊版 2 顆 / 1.4 秒 ＝ 1.43 顆/秒，
         而滿級 `w-mine` 是 7 顆 / 2.75 秒 ＝ **2.55 顆/秒**，單顆傷害還只差一成。
         「拿掉限制」的設計意圖是對的，但沒有人核對過拿掉之後的吞吐量。
         現在是 3 顆 / 0.85 秒 ＝ 3.53 顆/秒。

         等級（2026-08-05，`CRAFT_MAX`）：
           Lv2  4 顆、單顆 146（吞吐 +33%）
           Lv3  5 顆、單顆 160，**而且一半改投到敵群腳下**（見下）

         ⚠️ 單靠顆數不夠：地雷池只有 18 顆（`world/pools.ts`）而且是
         `w-mine`／`e-limpet`／機體小招共用的，佈得再密也塞不進去 ——
         `pnpm crafts` 量到只有 ×1.45（帶寬是 1.6–1.9×）。
         所以另一半的成長給單顆傷害，那一條不吃池子。 */
      var ar = P.s.area || 1, n = 2 + lv, dmg = 118 + lv * 14;
      /* Lv3 拿掉的第二條限制：**雷區只在我腳下**。
         在那之前這把武裝要求玩家「先鋪路，再把敵人引過來」——
         引不過來的那些（遠程型、繞圈的）它一顆都碰不到。
         Lv3 之後一半的雷直接佈在最近的敵群腳下，佈場從「等他們來」
         變成「我去圍你」。找不到目標就退回原本的腳下佈設。 */
      var far = lv >= 3 ? nearestEnemy(P.pos, 34, undefined, false) : null;
      for (var k = 0; k < n; k++) {
        /* **取池子放在擲骰之前**（原本的順序）。倒過來的話「池子滿了」
           那一幀會多消耗兩顆骰子，同一顆種子的後續就整個漂掉。 */
        var mn = mines.get(); if (!mn) break;
        var a = rr(0, TAU), d = rr(4, 9);
        var cx = P.pos.x, cz = P.pos.z;
        if (far && k % 2 === 1) { cx = far.pos.x; cz = far.pos.z; d = d * 0.4; }
        var sp = safeSpot(cx + Math.cos(a) * d, cz + Math.sin(a) * d, 1);
        mn.m.position.set(sp.x, 0, sp.z); mn.m.scale.setScalar(0.01);
        mn.life = 0; mn.arm = 0.3; mn.dmg = dmg; mn.who = 'e-minefield';
        /* **這三個欄位一定要設。** 地雷是池化物件，不設的話會沿用
           上一顆用過這個槽的地雷的值（`sim/projectiles.ts:167` 的 `||`
           預設只在 falsy 時生效，而殘留值不是 falsy）——
           於是這把武裝的行為取決於「前一顆是誰放的」。 */
        mn.trigR = 6.4 * ar;
        mn.blast = 6.0 * ar;
        mn.ttl = 14 * (P.s.dur || 1);
        scene.add(mn.m);
      }
    }
  },
  {
    /* 環繞旋刃 + 時間壓縮 → 斬空領域。
       **拿掉的限制：「傷害只發生在刀刃碰得到的那一圈」。**
       原本旋刃是純接觸判定，離圈子遠一點的敵人完全安全 ——
       它的射程等於它的半徑，這是它最大的天花板。進化之後每轉一圈
       往外拋一輪斬波，**射程與半徑脫鉤**，防禦圈變成攻擊圈。 */
    id: 'e-voidring', n: '斬空領域', kind: '進化', axes: ['範圍', '衝擊'],
    evo: { from: 'w-orbit', needs: 'p-time' },
    turn: 3, turnD: '環帶 → 整個圓盤（站在圈內的敵人不再安全）',
    d: function (lv) {
      return '**射程不再等於半徑**：六片旋刃環繞，每轉一圈往外拋出一輪斬波'
        + (lv < 3 ? '' : '；**判定從環帶變成整個圓盤**，圈內也絞');
    },
    /* **沒有角度判定**（下面只比半徑），所以造型畫的是一整條環帶而不是六個扇形。
       六片刀刃留著當動態，但「哪裡會受傷」由那條帶講。 */
    onEquip: function (o) { o.st = { vis: makeOrbitBlades(6, 0x9fffe8, { width: 2.0 }), a: 0, hits: [], last: 0 }; },
    onRemove: function (o) { if (o.st) disposeGadget(o.st.vis); o.st = null; },
    update: function (lv, dt, o) {
      if (!o.st) return;
      /* 等級（2026-08-05）：
           Lv2  轉速 4.2 → 5.2（每分鐘多轉 24% 圈 ＝ 斬波多 24%），接觸 44 → 52
           Lv3  判定從環帶變成圓盤（見下），斬波 6 → 8 發 */
      var st = o.st, R = 8.5, n = 6;
      st.a += dt * (lv < 2 ? 4.2 : 5.2);
      st.vis.g.position.set(P.pos.x, 0, P.pos.z);
      setBladeRadius(st.vis, R);
      for (var b = 0; b < n; b++) {
        st.vis.blades[b].visible = true;
        st.vis.blades[b].rotation.y = st.a + (b / n) * TAU;
      }
      for (var h = st.hits.length - 1; h >= 0; h--) {
        st.hits[h].t -= dt;
        if (st.hits[h].t <= 0) st.hits.splice(h, 1);
      }
      // 接觸傷害
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
        /* Lv3 拿掉的第二條限制：**傷害只發生在那一圈上**。
           在那之前貼到玩家身上的敵人（衝鋒型、自爆型）站在環的內側完全安全 ——
           一把叫「領域」的武裝，圈內卻是安全區。Lv3 之後 |d−R| 的環帶判定
           換成 d ≤ R 的圓盤判定，領域名副其實。 */
        if (lv >= 3 ? d > R + 2.0 + e.r : Math.abs(d - R) > 2.0 + e.r) continue;
        var busy = false;
        for (var q = 0; q < st.hits.length; q++) if (st.hits[q].e === e) { busy = true; break; }
        if (busy) continue;
        st.hits.push({ e: e, t: 0.35 });
        var kk = 1 / Math.max(0.6, d);
        damageEnemy(e, lv < 2 ? 44 : 52, false, dx * kk * 6, dz * kk * 6, 'vortex', undefined, undefined, 'e-voidring');
      }
      // 每轉一圈拋一輪斬波
      var turns = Math.floor(st.a / TAU);
      if (turns > st.last) {
        st.last = turns;
        var waves = lv < 3 ? 6 : 8;
        for (var w = 0; w < waves; w++) {
          spawnBullet(P.pos.x, P.pos.z, (w / waves) * TAU, 38, false, 40, 99, 0, 1.9, 0.6, 'e-voidring');
        }
        spawnRing(P.pos.x, P.pos.z, 0x9fffe8, 2, 18, 0.35);
        SFX.slash();
      }
    }
  },
  {
    /* 跳彈迴路 + 動能穿甲 → 穿甲協定。**衝擊軸的頂點之一。**
       **拿掉的限制：「跳彈需要有下一個目標」。**
       原本跳彈打單一硬目標時只跳一次就沒了 —— 場上只有一隻 Boss 的時候
       它是六把裡最弱的。進化之後沒有別的目標就**折返打同一隻**，
       所以它從「怕沒目標」變成「最怕它的就是單一硬目標」，正好反過來。 */
    id: 'e-piercer', n: '穿甲協定', kind: '進化', axes: ['衝擊', '冷卻'],
    evo: { from: 'w-chain', needs: 'p-impact' },
    turn: 3, turnD: '只會跳 → 另加一根貫穿整排的重釘（不跳、直接穿過去）',
    d: function (lv) {
      return '**沒有下一個目標時折返**：跳彈在同一個目標上反覆穿刺，傷害 '
        + (lv < 2 ? 58 : 68) + (lv < 3 ? '' : '；**另加一根貫穿整排的重釘**');
    },
    cd: function (lv) { return lv < 2 ? 0.36 : 0.30; },
    fire: function (lv) {
      /* ⚠️ 2026-08-05 配平（`pnpm crafts` 量到 ×0.82）。真因是**發數比來源少**：
         滿級 `w-chain` 是 2 發 / 0.55 秒 ＝ 3.64 發/秒，而舊版這裡是
         1 發 / 0.42 秒 ＝ 2.38 發/秒，單發傷害也只差一點（34 vs 35）。
         「沒有目標時折返」是真的拿掉了一條限制，但那條限制只在單體場合有價值，
         而配平是拿整體 DPS 在量的。現在 2 發 / 0.36 秒 ＝ 5.56 發/秒。

         等級（2026-08-05）：
           Lv2  冷卻 0.36 → 0.30、單發 58 → 68（吞吐 +41%）
           Lv3  多一根不跳彈的貫穿重釘（見下） */
      for (var i = 0; i < 2; i++) {
        /* 第一發給鎖定目標，第二發找最近的 —— 兩發都打同一隻的話
           「彈向下一個」就沒有意義了（跟 `w-chain` 同一個理由）。 */
        var tgt = (i === 0 && lockedTarget()) || nearestEnemy(P.pos, 40, P.faceAim + (i ? 1.2 : 0), true);
        if (!tgt) return;
        var a = Math.atan2(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z);
        var crit = RNG.next() < P.s.crit;
        /* chain 給 99：`sim/projectiles.ts` 的跳彈在找不到新目標時會回頭找舊的，
           所以「無上限」在單體上就自然變成折返穿刺。 */
        spawnBullet(P.pos.x, P.pos.z, a, (lv < 2 ? 58 : 68) * (crit ? P.s.critMul : 1), crit, 62, 0, 99, 0.6, undefined, 'e-piercer');
        /* Lv3 拿掉的第二條限制：**它只會跳，不會穿**。跳彈每一跳都要重新找目標，
           一次只處理一隻；密集直線（走廊、衝鋒隊列）對它反而是最差的形狀。
           Lv3 之後每一發旁邊多一根 `chain = 0` 的重釘，直接貫穿一整排。

           **必須另外開一發**：`chain > 0` 時 `pierce` 永遠不生效
           （`sim/projectiles.ts` 的跳彈分支會先把子彈收掉），
           在同一發上同時給兩個參數只會靜靜地什麼都沒發生。 */
        if (lv >= 3) {
          spawnBullet(P.pos.x, P.pos.z, a, 40 * (crit ? P.s.critMul : 1), crit, 70, 6, 0, 1.1, 1.4, 'e-piercer');
        }
      }
      SFX.shoot();
    }
  },
  {
    /* 軌道光柱 + 過載鎖定 → 軌道支配。**衝擊軸的頂點之二。**
       **拿掉的限制：「要等冷卻，而且落點是猜的」。**
       原本光柱最煩的是它砸下來的時候敵人已經走開了（0.7 秒預告 + 9 秒冷卻）。
       進化之後冷卻縮到 1.6 秒、預告縮到 0.35 秒，而且**踉蹌中的目標必定被照到** ——
       它從「亂數清場」變成「處決確認」。 */
    id: 'e-dominion', n: '軌道支配', kind: '進化', axes: ['衝擊', '傷害'],
    evo: { from: 'w-orbital', needs: 'p-stagger' },
    turn: 3, turnD: '一次一根 → 同時落兩根（第二根給下一個踉蹌／精英目標）',
    d: function (lv) {
      return '**踉蹌中的目標必定被照到**：冷卻 ' + (lv < 2 ? '1.35' : '1.05') + ' 秒，單發 '
        + (lv < 2 ? 148 : (lv < 3 ? 170 : 196)) + (lv < 3 ? '' : '；**同時落下兩根**');
    },
    /* ⚠️ 2026-08-05 配平（量到 ×2.42，差一點就進帶寬）：冷卻 1.6 → 1.35、
       單發 120 → 148。這一條本來就最接近，只補到帶寬下緣。
       等級：Lv2 冷卻 1.35 → 1.05（吞吐 +29%）；Lv3 同時兩根（見 `strike()`）。 */
    cd: function (lv) { return lv < 2 ? 1.35 : 1.05; },
    fire: function (lv) {
      /* Lv3 拿掉的第二條限制：**一次只照一個**。這把武裝的定位是「處決確認」，
         但踉蹌是會同時發生在好幾隻身上的（衝擊軸的 build 尤其）——
         排在第二個的那隻在光柱回來之前早就站起來了。
         Lv3 之後同一次開火落兩根，第二根給下一個踉蹌／精英目標；
         只有一個目標時第二根不發（不重複照同一隻，那只是傷害不是覆蓋）。 */
      var first = dominionStrike(lv, null);
      if (lv >= 3 && first) dominionStrike(lv, first);
    }
  }
];

/** 軌道支配的單根光柱，回傳被照到的那一隻（給第二根當「不要重複」用）。
    抽成函式是為了 Lv3 的第二根 —— 複製一份的話兩根的配平會各自漂移。 */
function dominionStrike(lv: number, skip: any) {
  /* 先找踉蹌中的（必中），沒有才退回精英／最近的。 */
  var tgt = null, i;
  for (i = 0; i < enemies.length; i++) if (enemies[i].staggerT > 0 && enemies[i] !== skip) { tgt = enemies[i]; break; }
  if (!tgt) for (i = 0; i < enemies.length; i++) if ((enemies[i].elite || enemies[i].T.boss) && enemies[i] !== skip) { tgt = enemies[i]; break; }
  /* 退回「最近的」時要**自己排除 skip**：`nearestEnemy` 不認得它，
     不排除的話第二根會照在第一根同一隻身上（那是傷害不是覆蓋）。 */
  if (!tgt) {
    var best = nearestEnemy(P.pos, 46, undefined, false);
    tgt = (best && best !== skip) ? best : null;
  }
  if (!tgt) return null;
  var s2 = strikes.get(); if (!s2) return null;
  s2.m.position.set(tgt.pos.x, 0, tgt.pos.z);
  /* 單發傷害也吃等級。⚠️ 只靠「冷卻 + 第二根」量到 ×1.24（帶寬 1.6–1.9×）——
     光柱池只有 10 個（`world/pools.ts`，四把武裝共用），一次射兩根之後
     池子就是瓶頸，再縮冷卻也擠不進去。 */
  s2.radius = 7.5 * (P.s.area || 1); s2.dmg = lv < 2 ? 148 : (lv < 3 ? 170 : 196);
  s2.who = 'e-dominion';
  s2.life = 0; s2.delay = 0.35; s2.fired = false;
  s2.ring.scale.setScalar(s2.radius); s2.ring.material.opacity = 0.8;
  s2.col.scale.set(s2.radius * 1.2, 60, s2.radius * 1.2); s2.col.material.opacity = 0;
  scene.add(s2.m);
  /* **標定的那一拍。** 這條進化的賣點是「踉蹌中的目標必定被照到」，
     而預告只有 0.35 秒 —— 沒有一個「鎖上了」的訊號的話，玩家讀到的
     只是光柱掉得比較快。收縮的框（r0 > r1）是標定的語意，
     跟擴散的衝擊環剛好相反。 */
  fxShockRing(tgt.pos.x, tgt.pos.z, 0, s2.radius * 1.6, s2.radius * 0.85, 0.3, 0.9);
  SFX.beam();
  return tgt;
}
